import { useState } from 'react'
import { data, Link, useLoaderData } from 'react-router'
import { trackClient } from '#app/analytics/client.ts'
import { type RouteAnalyticsHandle } from '#app/analytics/constants.ts'
import {
	type ListingEventContext,
	listingLinkHandler,
	useBusinessProfileView,
} from '#app/analytics/listing.ts'
import { AdSlot } from '#app/components/ads/ad-slot.tsx'
import {
	BusinessListing,
	BusinessListingGroup,
} from '#app/components/business-listing.tsx'
import { ClaimEvidence } from '#app/components/claim-evidence.tsx'
import { Dateline } from '#app/components/dateline.tsx'
import { FeedbackForm } from '#app/components/feedback/feedback-form.tsx'
import { QuoteRequestForm } from '#app/components/quote-request-form.tsx'
import { RatingDistribution } from '#app/components/rating-distribution.tsx'
import { ScoreMark } from '#app/components/score.tsx'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { Alert, AlertDescription } from '#app/components/ui/alert.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { CopyButton } from '#app/components/ui/copy-button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.js'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { loadClaimsForBusinesses } from '#app/scoring/claims.server.ts'
import {
	RANKING_ELIGIBLE,
	categoryHref,
	getPublishableCitiesForCategory,
	getPublishablePages,
	getRankedCategorySlugs,
} from '#app/scoring/publishable-pages.server.ts'
import {
	DEPTH_FLOORS,
	type RatingDistribution as RatingDistributionData,
} from '#app/scoring/scoring-model.ts'
import { formatVerifiedAge } from '#app/scoring/verification.ts'
import { useCanonical } from '#app/seo/hooks.ts'
import { profileRobots } from '#app/seo/profile-indexing.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import {
	buildFaqLd,
	buildLocalBusinessLd,
	businessDescription,
} from '#app/seo/structured-data.ts'
import { getAdForLocation } from '#app/utils/ads.server.ts'
import { categoryPlural } from '#app/utils/category-name.ts'
import { prisma } from '#app/utils/db.server.ts'
import { formatPhoneNumber, pluralize } from '#app/utils/misc.tsx'
import { quoteRequestAction } from '#app/utils/quote-request.server.ts'
import { type Route } from './+types/$businessSlug.ts'

export async function loader({ params }: Route.LoaderArgs) {
	const { businessSlug } = params

	const business = await prisma.business.findUnique({
		where: { slug: businessSlug, status: 'published' },
		select: {
			id: true,
			name: true,
			isFeatured: true,
			slug: true,
			description: true,
			address: true,
			phone: true,
			website: true,
			createdAt: true,
			ownerId: true,
			aiDescription: true,
			aiFaqSnippets: true,
			highlights: true,
			aiTopics: true,
			aiServices: true,

			// Scoring, synthesis and verification, so the destination of every
			// ranking-page link reflects what the ranking said about it.
			score: true,
			scoreComputedAt: true,
			// 100% coverage across the catalog, and the defining content of a
			// page competing for "{business} reviews".
			ratingDist: true,
			ratingValue: true,
			ratingCount: true,
			reviewsHeld: true,
			bestFor: true,
			whyItRanks: true,
			pros: true,
			watchOuts: true,
			synthesisClaims: true,
			// Not rendered — read by `clearsProfileFloor` in the meta builder to
			// decide whether this profile may be indexed at all.
			synthesisAt: true,
			lastVerifiedAt: true,
			isClosed: true,
			// Needed to judge whether THIS business appears on a ranking, which
			// is a separate question from whether the ranking page exists.
			excludeFromRankings: true,
			featuredMedia: {
				select: {
					id: true,
					name: true,
					title: true,
					altText: true,
					url: true,
				},
			},
			categories: {
				select: {
					id: true,
					name: true,
					pluralName: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					pluralName: true,
					slug: true,
				},
			},
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			seoMeta: {
				select: {
					title: true,
					description: true,
					canonicalUrl: true,
					focusKeyword: true,
					seoKeywords: true,
					noIndex: true,
					ogTitle: true,
					ogDescription: true,
					ogImage: true,
					ogType: true,
					twitterTitle: true,
					twitterDescription: true,
					twitterImage: true,
					twitterCard: true,
					structuredData: true,
				},
			},
		},
	})

	if (!business) {
		throw new Response('Business not found', { status: 404 })
	}

	// Fetch related businesses
	const RELATED_LIMIT = 3

	const subcategoryIds = business.subcategories.map((s) => s.id)
	const categoryIds = business.categories.map((c) => c.id)

	const selectForCard = {
		id: true,
		name: true,
		slug: true,
		isFeatured: true,
		description: true,
		aiDescription: true,
		phone: true,
		website: true,
		address: true,
		// The shared listing row leads with measurement, so it needs the
		// numbers. Without these every related business rendered as a name and
		// a blurb on a site whose whole claim is that it measures things.
		score: true,
		ratingValue: true,
		ratingCount: true,
		bestFor: true,
		categories: { select: { id: true, name: true, pluralName: true, slug: true } },
		subcategories: {
			select: { id: true, name: true, pluralName: true, slug: true },
		},
		cities: { select: { id: true, name: true, slug: true } },
	} as const

	const subMatches = subcategoryIds.length
		? await prisma.business.findMany({
				where: {
					id: { not: business.id },
					status: 'published',
					subcategories: { some: { id: { in: subcategoryIds } } },
				},
				select: selectForCard,
				take: 30,
			})
		: []

	const catMatches = categoryIds.length
		? await prisma.business.findMany({
				where: {
					id: { not: business.id },
					status: 'published',
					categories: { some: { id: { in: categoryIds } } },
				},
				select: selectForCard,
				take: 30,
			})
		: []

	function shuffleInPlace<T>(items: T[]) {
		for (let i = items.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1))
			const temp = items[i]
			const tempJ = items[j]
			if (temp !== undefined && tempJ !== undefined) {
				items[i] = tempJ
				items[j] = temp
			}
		}
	}

	const byId = new Map<string, (typeof subMatches)[number]>()
	for (const b of [...subMatches, ...catMatches])
		if (!byId.has(b.id)) byId.set(b.id, b)
	const candidates = Array.from(byId.values())

	/*
	 * Shuffled as one pool. Featured businesses used to be shuffled into their
	 * own bucket and concatenated in front, which is money buying position in
	 * a visible ordering — the same violation as the category shortlist, just
	 * quieter because the list is short and the order looks arbitrary anyway.
	 *
	 * No query that produces an ordering may read `isFeatured`.
	 */
	shuffleInPlace(candidates)
	const relatedBusinesses = candidates.slice(0, RELATED_LIMIT)

	// Get ad for sidebar
	const ad = await getAdForLocation('BUSINESS_SIDEBAR_ABOVE_RELATED', {
		categories: business.categories,
		subcategories: business.subcategories,
		cities: business.cities,
		pageType: 'business',
		businessId: business.id,
	})

	/*
	 * Ranking pages that this business appears on.
	 *
	 * The link graph was one-way: rankings pointed down to listings and
	 * listings barely pointed back. That wastes equity and leaves the
	 * corroboration incomplete, since a crawler landing on a listing had no
	 * path to the page that ranks it.
	 *
	 * Uses the same publishable helpers as the sitemap, so a link is only
	 * rendered when the destination will actually render a ranking.
	 */
	/*
	 * Two separate questions, and both must be yes:
	 *   1. Would this business appear on a ranking? (subject eligibility)
	 *   2. Does that ranking page exist? (destination publishability)
	 *
	 * Checking only the second let a closed or excluded business advertise
	 * "Ranked in: Best Roofing Contractors in DFW" directly above its own
	 * notice saying it had been removed from the rankings.
	 *
	 * Mirrors RANKING_ELIGIBLE, applied to the loaded row rather than as a
	 * query filter.
	 */
	const isRankingEligible =
		business.score != null &&
		!business.isClosed &&
		!business.excludeFromRankings

	/*
	 * categories[0] is whatever Prisma returns first. Adequate while businesses
	 * carry a single category; when multi-category becomes common this should
	 * pick deliberately, or render every publishable category.
	 */
	const primaryCategory = isRankingEligible ? business.categories[0] : undefined
	const [metroScored, publishableCities] = primaryCategory
		? await Promise.all([
				prisma.business.count({
					where: {
						...RANKING_ELIGIBLE,
						categories: { some: { slug: primaryCategory.slug } },
					},
				}),
				getPublishableCitiesForCategory(primaryCategory.slug),
			])
		: [0, []]

	const businessCitySlugs = new Set(business.cities.map((c) => c.slug))
	const rankedIn = {
		category: primaryCategory ?? null,
		metroPublishable: metroScored >= DEPTH_FLOORS.bestMetro,
		cities: publishableCities.filter((c) => businessCitySlugs.has(c.slug)),
	}

	const claimsByBusiness = await loadClaimsForBusinesses([business])
	const claims = claimsByBusiness.get(business.id) ?? {
		pros: [],
		watchOuts: [],
	}

	/*
	 * Where each category badge should point.
	 *
	 * Resolved here rather than in the component because categoryHref lives
	 * beside the Prisma queries that decide it, and a listing page must not
	 * link into the /categories/{slug} → /best/{slug} redirect. With ~4,873
	 * listings these badges are the site's highest-volume category links, so a
	 * hop here is the most expensive one to leave in place.
	 *
	 * Subcategory slugs are included deliberately: they are never in the ranked
	 * set, so they resolve to their own hub, which is the correct destination.
	 */
	/*
	 * Newest review held for this business, so the listing states how current
	 * its evidence is rather than implying the page itself was rewritten today.
	 */
	const newestReview = await prisma.review.findFirst({
		where: { businessId: business.id, timestamp: { not: null } },
		orderBy: { timestamp: 'desc' },
		select: { timestamp: true },
	})

	const rankedSlugs = await getRankedCategorySlugs()

	/*
	 * The city x category pages this business would actually appear on.
	 *
	 * Gated on getPublishablePages rather than emitted as a cross product: a
	 * pair below the depth floor 301s, and linking it from every listing sent a
	 * large share of the site's internal links into a redirect.
	 */
	const { cityCategories } = await getPublishablePages()
	const publishablePairs = new Set(
		cityCategories.map((pair) => `${pair.citySlug}/${pair.categorySlug}`),
	)
	const rankingLinks = business.cities.flatMap((city) =>
		[...business.categories, ...business.subcategories].flatMap((category) =>
			publishablePairs.has(`${city.slug}/${category.slug}`)
				? [
						{
							href: `/${city.slug}/${category.slug}`,
							label: `${categoryPlural(category)} in ${city.name}`,
						},
					]
				: [],
		),
	)
	const categoryHrefs = Object.fromEntries(
		[
			...business.categories,
			...business.subcategories,
			...relatedBusinesses.flatMap((related) => related.categories),
		].map((category) => [
			category.slug,
			categoryHref(category.slug, rankedSlugs),
		]),
	)

	return data({
		business,
		dateline: {
			reviewsThrough: newestReview?.timestamp?.toISOString() ?? null,
			scoredAt: business.scoreComputedAt?.toISOString() ?? null,
			reviewsRead: business.reviewsHeld ?? undefined,
		},
		relatedBusinesses,
		ad,
		claims,
		rankedIn,
		rankingLinks,
		categoryHrefs,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
/**
 * The quote request posts back to the listing it was rendered on, so it works
 * without JavaScript and a failure keeps the reader on the business they were
 * reading about.
 */
export async function action({ request, context }: Route.ActionArgs) {
	return quoteRequestAction({ request, context })
}

export const handle = {
	// Read by useRouteAnalytics in root.tsx; tags every pageview and
	// client event fired on this route with its page archetype.
	analytics: { pageType: 'business_profile' } satisfies RouteAnalyticsHandle,
	breadcrumb({ data }: { data: Awaited<ReturnType<typeof loader>>['data'] }) {
		const business = data?.business
		return business
			? { name: business.name, url: `/business/${business.slug}` }
			: null
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
		params: Record<string, string>
		pathname: string
	}): SeoProps | null {
		const business = loaderData?.business
		if (!business) return null

		const seo = business.seoMeta
		const cityNames = business.cities.map((c) => c.name).join(', ')
		const canonicalUrl =
			seo?.canonicalUrl || `${origin}/business/${business.slug}`
		/*
		 * Same precedence as the page body and the JSON-LD: the synthesized
		 * paragraph first. A meta description drawn from generic copy while the
		 * page shows grounded analysis gives search and answer engines the
		 * weaker summary of a page whose whole value is the stronger one.
		 */
		const defaultDescription =
			businessDescription(business) ||
			`Find ${business.name} in ${cityNames}. Contact information and details for local services.`

		// Build keywords from focus keyword, SEO keywords, and categories
		const keywords = [
			seo?.focusKeyword,
			...(seo?.seoKeywords || []),
			...business.categories.map((c) => c.name),
		].filter(Boolean)

		// Build structured data
		const structuredData: Array<object | string> = [
			buildLocalBusinessLd(business, canonicalUrl),
		]

		const faq = Array.isArray(business.aiFaqSnippets)
			? (business.aiFaqSnippets as unknown[]).filter(isFaqItem)
			: []
		if (faq.length > 0) structuredData.push(buildFaqLd(faq))

		// Append custom structured data from DB if present
		if (seo?.structuredData) {
			const customData = seo.structuredData
			if (Array.isArray(customData)) {
				structuredData.push(...(customData as Array<object | string>))
			} else if (customData !== null) {
				structuredData.push(customData as object | string)
			}
		}

		/*
		 * Brand name first, reviews modifier second.
		 *
		 * The brand query is roughly twenty times the reviews query — "elevated
		 * roofing" runs 2,400 a month against 110 for "elevated roofing reviews"
		 * — so leading with the modifier would trade a large navigational query
		 * for a small commercial one. Carrying both wins both, and the review
		 * count makes the title a promise the page actually keeps rather than a
		 * restatement of the business name.
		 *
		 * The old title ended in the bare domain, which spent the most valuable
		 * characters in the SERP on a string nobody searches.
		 */
		const reviewedTitle = business.ratingCount
			? `${business.name} Reviews: What ${business.ratingCount.toLocaleString()} Customers Said | DFW Professionals`
			: `${business.name} in ${cityNames} | DFW Professionals`

		return {
			title: seo?.title || reviewedTitle,
			description: seo?.description || defaultDescription,
			canonical: canonicalUrl,
			keywords: keywords.length > 0 ? keywords : undefined,
			robots: profileRobots({ ...business, seoNoIndex: seo?.noIndex }),
			openGraph: {
				title: seo?.ogTitle || business.name,
				description: seo?.ogDescription || defaultDescription,
				/*
				 * The generated card, ahead of the business's own featured photo.
				 *
				 * This is our page about them, not their page. A business's
				 * promotional shot previewing under this headline reads as the
				 * business's own listing and quietly transfers the credibility of
				 * an independent assessment to the subject of it. The card says
				 * how many of their reviews we read, which is a fact about our
				 * work and unmistakably ours.
				 *
				 * An explicit ogImage still wins, because that is an editor
				 * deciding on purpose.
				 */
				image: seo?.ogImage || `${origin}/og/business/${business.slug}.png`,
				type: seo?.ogType || 'website',
				url: canonicalUrl,
			},
			twitter: {
				card:
					(seo?.twitterCard as 'summary_large_image' | 'summary') ||
					'summary_large_image',
				title: seo?.twitterTitle || business.name,
				description: seo?.twitterDescription || defaultDescription,
				// Same card as Open Graph, for the same reason.
				image:
					seo?.twitterImage || `${origin}/og/business/${business.slug}.png`,
			},
			structuredData,
		}
	},
}

export default function BusinessPage() {
	const {
		business,
		dateline,
		rankingLinks,
		relatedBusinesses,
		ad,
		claims,
		rankedIn,
		categoryHrefs,
	} = useLoaderData<typeof loader>()
	const canonicalUrl = useCanonical(`/business/${business.slug}`)
	const [flagDialogOpen, setFlagDialogOpen] = useState(false)

	// The category the "More local ___" heading is about. Optional because a
	// related listing may carry no category; the heading falls back to a generic
	// word rather than rendering "More local  in Dallas-Fort Worth".
	const relatedCategory = relatedBusinesses[0]?.categories[0] ?? null

	// Analytics context shared by every listing event on this page. These are the
	// numbers an owner sees in /manage and that we quote when selling placement.
	const listingCtx: ListingEventContext = {
		businessId: business.id,
		pageType: 'business_profile',
		categorySlug: business.categories[0]?.slug,
		citySlug: business.cities[0]?.slug,
	}

	useBusinessProfileView(listingCtx)

	// Highlights are now deterministic strings from attributes
	const highlights = (business.highlights as string[]) || []
	const topics = business.aiTopics || ''
	const services = Array.isArray(business.aiServices)
		? (business.aiServices as unknown[]).filter(isAiServiceItem)
		: []
	const faq = Array.isArray(business.aiFaqSnippets)
		? (business.aiFaqSnippets as unknown[]).filter(isFaqItem)
		: []

	// Structured data is emitted by handle.seo, not here.

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="grid gap-8 lg:grid-cols-3">
					{/* Main Content */}
					<div className="lg:col-span-2">
						<h1 className="mb-3 font-serif text-4xl tracking-tight">
							{business.name}
						</h1>

						{/*
						 * Dates without the byline. The author owns the rubric and the
						 * standards, not a judgment about this individual business, and
						 * a byline here would imply someone assessed this listing
						 * personally.
						 */}
						<Dateline stats={dateline} showByline={false} />

						<div className="text-muted-foreground mb-6 flex flex-col gap-2 text-sm md:flex-row md:items-center md:gap-6">
							<div className="flex items-center gap-2">
								<Icon name="map-pin" />
								{business.address && (
									<a
										href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
											`${business.name} ${business.address}`,
										)}`}
										target="_blank"
										rel="noopener noreferrer"
										className="hover:underline"
										{...listingLinkHandler('business_directions_clicked', {
											...listingCtx,
											placement: 'header',
										})}
									>
										{business.address}
									</a>
								)}
							</div>

							{business.phone && (
								<div className="flex items-center gap-2">
									<Icon name="phone" />
									<a
										href={`tel:${business.phone}`}
										className="hover:underline"
										{...listingLinkHandler('business_phone_clicked', {
											...listingCtx,
											placement: 'header',
										})}
									>
										{formatPhoneNumber(business.phone)}
									</a>
								</div>
							)}

							{business.website && (
								<div className="flex items-center gap-2">
									<Icon name="link-2" />
									<a
										href={business.website}
										target="_blank"
										rel="noopener noreferrer"
										className="hover:underline"
										{...listingLinkHandler('business_website_clicked', {
											...listingCtx,
											placement: 'header',
										})}
									>
										Visit Website
									</a>
								</div>
							)}
						</div>

						{/*
						 * The judgment first, then what the business looks like.
						 *
						 * The score sat in a bordered box below the contact line and the
						 * screenshot was buried under the review claims, so a reader met
						 * an address before they met either the verdict or the business
						 * itself. Borders are gone throughout: this page was a stack of
						 * cards inside a card grid, which framed every section equally
						 * and gave the page no order.
						 */}
						{business.score != null ? (
							<div className="mb-6 flex items-stretch gap-4">
								<ScoreMark score={business.score} size="lg" />
								<div className="min-w-0 flex-1">
									<Link
										className="text-muted-foreground text-sm underline underline-offset-4"
										to="/scoring-method"
									>
										DFW Professionals score
									</Link>
									<div className="text-muted-foreground mt-1.5 flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-sm tabular-nums">
										{business.ratingValue != null ? (
											<span>
												{business.ratingValue.toFixed(1)}★{' '}
												<span className="text-muted-foreground/70">
													{(business.ratingCount ?? 0).toLocaleString()} reviews
												</span>
											</span>
										) : null}
										{business.reviewsHeld ? (
											<span>{business.reviewsHeld} read in full</span>
										) : null}
									</div>
									{business.bestFor ? (
										<p className="text-muted-foreground mt-1.5 text-sm">
											Best for: {business.bestFor}
										</p>
									) : null}
								</div>
							</div>
						) : null}

						{business.featuredMedia && (
							<div className="mb-6">
								{business.website ? (
									<a
										href={business.website}
										target="_blank"
										rel="noopener noreferrer"
										className="group hover:border-ring hover:outline-border/50 relative block self-stretch overflow-clip rounded-md border outline-transparent transition-all duration-300 ease-out hover:outline-4"
										{...listingLinkHandler('business_website_clicked', {
											...listingCtx,
											placement: 'featured_media',
										})}
									>
										<img
											src={business.featuredMedia.url || ''}
											alt={business.featuredMedia.altText || business.name}
											loading="lazy"
											decoding="async"
											className="aspect-video h-auto w-full object-cover object-top transition-transform duration-300 ease-out will-change-transform group-hover:scale-[101%]"
										/>
									</a>
								) : (
									<div className="relative overflow-clip rounded-md border">
										<img
											src={business.featuredMedia.url || ''}
											alt={business.featuredMedia.altText || business.name}
											loading="lazy"
											decoding="async"
											className="aspect-video h-auto w-full object-cover object-top"
										/>
									</div>
								)}
							</div>
						)}

						{business.isClosed ? (
							<div className="mb-6 rounded-lg border p-4 text-sm">
								<p className="font-medium">
									Google reports this business as permanently closed.
								</p>
								<p className="text-muted-foreground mt-1">
									It has been removed from our rankings. If this is wrong,{' '}
									<Link className="underline underline-offset-4" to="/contact">
										tell us
									</Link>
									.
								</p>
							</div>
						) : null}

						{/*
						 * Verification and scoring are independent: a listing can be
						 * checked and reachable without having enough reviews to score.
						 * Gated only on a successful check.
						 *
						 * formatVerifiedAge returns null when we have never verified, and
						 * there is deliberately no fallback wording. A freshness claim we
						 * cannot back is worse than none.
						 */}
						{formatVerifiedAge(business.lastVerifiedAt) ? (
							<p className="text-muted-foreground mb-6 text-xs">
								Listing details verified{' '}
								{formatVerifiedAge(business.lastVerifiedAt)}.
							</p>
						) : null}

						{businessDescription(business) ? (
							<div className="mb-6">
								<p className="text-lg leading-relaxed">
									{businessDescription(business)}
								</p>
							</div>
						) : null}

						{rankedIn.category &&
						(rankedIn.metroPublishable || rankedIn.cities.length > 0) ? (
							<div className="mb-6 text-sm">
								<span className="text-muted-foreground">Ranked in: </span>
								{rankedIn.metroPublishable ? (
									<Link
										className="underline underline-offset-4"
										to={`/best/${rankedIn.category.slug}`}
									>
										Best {categoryPlural(rankedIn.category)} in DFW
									</Link>
								) : null}
								{rankedIn.metroPublishable && rankedIn.cities.length > 0
									? ', '
									: null}
								{rankedIn.cities.map((city, index) => (
									<span key={city.slug}>
										{index > 0 ? ', ' : null}
										<Link
											className="underline underline-offset-4"
											to={`/${city.slug}/${rankedIn.category!.slug}`}
										>
											{categoryPlural(rankedIn.category!)} in {city.name}
										</Link>
									</span>
								))}
							</div>
						) : null}

						{claims.pros.length > 0 || claims.watchOuts.length > 0 ? (
							<div className="mb-6 space-y-4">
								{claims.pros.length > 0 ? (
									<div>
										<h2 className="mb-2 font-serif text-lg tracking-tight">
											What reviewers consistently mention
										</h2>
										<ul className="space-y-2">
											{claims.pros.map((claim, i) => (
												<ClaimEvidence key={`pro-${i}`} claim={claim} />
											))}
										</ul>
									</div>
								) : null}

								{claims.watchOuts.length > 0 ? (
									<div>
										<h2 className="mb-2 font-serif text-lg tracking-tight">
											Watch out for
										</h2>
										<ul className="space-y-2">
											{claims.watchOuts.map((claim, i) => (
												<ClaimEvidence
													key={`watch-${i}`}
													claim={claim}
													tone="caution"
												/>
											))}
										</ul>
									</div>
								) : null}
							</div>
						) : null}

						<RatingDistribution
							ratingDist={business.ratingDist as RatingDistributionData | null}
							ratingValue={business.ratingValue}
							ratingCount={business.ratingCount}
						/>

						{topics && (
							<Alert variant="info" className="mb-6">
								<Icon name="check-circled" />
								<AlertDescription>{topics}</AlertDescription>
							</Alert>
						)}

						{services.length > 0 && (
							<div className="my-12">
								<h2 className="mb-6 font-serif text-xl tracking-tight">
									Services offered by {business.name}
								</h2>
								<dl className="grid grid-cols-1 gap-x-8 gap-y-8 lg:max-w-none lg:grid-cols-2">
									{services.map((service, idx) => (
										<div key={idx} className="relative flex flex-col pl-9">
											<dt className="text-foreground text-base font-semibold">
												<Icon
													name="handshake"
													className="absolute top-1 left-1 size-5"
												/>
												{service.service}
											</dt>
											{service.content && (
												<dd className="text-muted-foreground mt-1 flex flex-auto flex-col text-base">
													<p className="flex-auto leading-relaxed">
														{service.content}
													</p>
												</dd>
											)}
										</div>
									))}
								</dl>
							</div>
						)}

						{/* Highlights Section */}
						{highlights.length > 0 && (
							<div className="mb-12">
								<dl className="bg-muted/90 grid grid-cols-1 gap-0.5 overflow-hidden rounded-md text-center sm:grid-cols-2 lg:grid-cols-3">
									{highlights.map((highlight, idx) => (
										<div key={idx} className="bg-muted/90 flex flex-col p-6">
											<dd className="text-foreground text-sm leading-relaxed font-medium">
												<Icon
													name="check-circled"
													className="mr-2 size-4 text-green-500"
												/>
												{highlight}
											</dd>
										</div>
									))}
								</dl>
							</div>
						)}

						{faq.length > 0 && (
							<div className="mb-6">
								<h2 className="mb-4 font-serif text-lg tracking-tight">
									Frequently Asked Questions
								</h2>

								<Accordion
									type="single"
									collapsible
									className="w-full"
									/*
									 * Which questions get opened is the cheapest read on what a
									 * visitor wanted to know before contacting, and the
									 * AI-generated FAQ has no other feedback signal.
									 *
									 * The index is recovered by rebuilding the same `item-{i}`
									 * value the render below emits, rather than by parsing the
									 * string — so the two cannot drift, and an unrecognised
									 * value (including the '' emitted on collapse) simply
									 * fires nothing.
									 *
									 * Deliberately no $insert_id: re-opening the same question
									 * is repeat interest, and collapsing it into one event
									 * would hide exactly the confusion worth seeing.
									 */
									onValueChange={(value) => {
										const index = faq.findIndex(
											(_, idx) => `item-${idx}` === value,
										)
										if (index === -1) return
										trackClient('business_faq_expanded', {
											business_id: business.id,
											business_slug: business.slug,
											page_type: 'business_profile',
											question: faq[index]?.q,
											position: index + 1,
										})
									}}
								>
									{faq.map((item, idx) => (
										<AccordionItem key={idx} value={`item-${idx}`}>
											<AccordionTrigger>{item.q}</AccordionTrigger>
											<AccordionContent>
												<p className="text-muted-foreground text-sm leading-relaxed">
													{item.a}
												</p>
											</AccordionContent>
										</AccordionItem>
									))}
								</Accordion>
							</div>
						)}

						<div className="mb-6">
							<h3 className="text-muted-foreground mb-1 font-semibold">
								Categories
							</h3>
							<div className="flex flex-wrap gap-2">
								{business.categories.map((category) => (
									<Link
										key={category.id}
										to={
											categoryHrefs[category.slug] ??
											`/categories/${category.slug}`
										}
									>
										<Badge variant="outline" className="hover:bg-accent">
											{category.name}
										</Badge>
									</Link>
								))}
								{business.subcategories.map((subcategory) => (
									<Link
										key={subcategory.id}
										to={
											categoryHrefs[subcategory.slug] ??
											`/categories/${subcategory.slug}`
										}
									>
										<Badge variant="outline" className="hover:bg-accent">
											{subcategory.name}
										</Badge>
									</Link>
								))}
							</div>
						</div>
						<div className="mb-6">
							<h3 className="text-muted-foreground mb-1 font-semibold">
								Cities
							</h3>
							<div className="flex flex-wrap gap-2">
								{business.cities.map((city) => (
									<Link key={city.id} to={`/cities/${city.slug}`}>
										<Badge variant="outline" className="hover:bg-accent">
											{city.name}
										</Badge>
									</Link>
								))}
							</div>
						</div>

						<div className="mb-6 flex items-center justify-end gap-2">
							<CopyButton textToCopy={`${canonicalUrl}` || ''} />

							<Dialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen}>
								<Tooltip>
									<TooltipTrigger asChild>
										<DialogTrigger asChild>
											<Button variant="outline" size="sm">
												<Icon name="flag" />
											</Button>
										</DialogTrigger>
									</TooltipTrigger>
									<TooltipContent>
										<p>Send report/suggestion</p>
									</TooltipContent>
								</Tooltip>

								<DialogContent>
									<DialogHeader>
										<DialogTitle>Report an Issue</DialogTitle>
										<DialogDescription>
											Help us improve by reporting issues with this business
											listing.
										</DialogDescription>
									</DialogHeader>
									<FeedbackForm
										variant="flag"
										defaultValues={{
											businessId: business.id,
											businessName: business.name,
											pageUrl: canonicalUrl,
										}}
										onSuccess={() => setFlagDialogOpen(false)}
									/>
								</DialogContent>
							</Dialog>

							{!business.ownerId && (
								<Button asChild variant="outline" size="sm">
									<Link to={`/claim?businessId=${business.id}`}>
										<Icon name="hand" />
										Claim
									</Link>
								</Button>
							)}
						</div>
					</div>

					{/* Sidebar */}
					<div className="mb-8 space-y-6 self-start md:sticky md:top-24">
						{/* Ad Slot */}
						{ad && (
							<AdSlot ad={ad} locationKey="BUSINESS_SIDEBAR_ABOVE_RELATED" />
						)}

						{/*
						 * Where this business can be found in a ranking.
						 *
						 * Every pair used to be linked raw as /{city}/{category}, and
						 * most of those are below the depth floor and 301. Across 4,873
						 * listings that is a large share of the site's internal links
						 * landing on a redirect, so destinations are resolved the same
						 * way the homepage, search and the hubs resolve them.
						 */}
						{rankingLinks.length > 0 ? (
							<div>
								<h2 className="mb-3 font-serif text-lg tracking-tight">
									Where {business.name} ranks
								</h2>
								<ul className="divide-y border-y">
									{rankingLinks.map((link) => (
										<li key={link.href}>
											<Link
												to={link.href}
												className="group hover:bg-muted/40 -mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-2.5 transition-colors"
											>
												<span className="text-sm group-hover:underline">
													{link.label}
												</span>
												<Icon
													name="chevron-right"
													className="text-muted-foreground size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
												/>
											</Link>
										</li>
									))}
								</ul>
							</div>
						) : null}

						{/*
						 * Shown on every listing, claimed or not.
						 *
						 * Nothing routes to the business yet — requests go to the
						 * operator — so suppressing the form on claimed listings would
						 * only lose demand data while delivering nothing extra to the
						 * owner. Named demand for a specific business is also the whole
						 * sales motion: "three people asked for a quote from you this
						 * month" is a better opening than any pitch.
						 */}
						{/*
						 * Only where the listing has a category. QuoteRequest.categoryId is
						 * a required FK, so a category-less listing would otherwise render
						 * a form that can never succeed.
						 */}
						{business.categories[0] ? (
							<div className="mb-8">
								<QuoteRequestForm
									categoryId={business.categories[0].id}
									categoryName={business.categories[0].name}
									cityId={business.cities[0]?.id}
									cityName={business.cities[0]?.name}
									businessId={business.id}
									businessName={business.name}
									sourcePath={`/business/${business.slug}`}
									variant="card"
								/>
							</div>
						) : null}

						{/*
						 * The owner path, live rather than commented out.
						 *
						 * This is the top of the revenue funnel described in
						 * docs/marketing-strategy.md §6a: an owner who has just read
						 * their own score and the cautions under it is the most
						 * motivated visitor this page ever gets. It offers to correct
						 * the listing, never to change the ranking.
						 */}
						{!business.ownerId ? (
							<div className="border-t pt-6">
								<h2 className="mb-2 font-serif text-lg tracking-tight">
									Own this business?
								</h2>
								<p className="text-muted-foreground mb-4 text-sm leading-relaxed">
									Claim the listing to correct your details and respond to what
									reviewers raised. Claiming cannot change your score or your
									position.
								</p>
								<Button asChild variant="outline" size="sm">
									<Link to={`/claim?businessId=${business.id}`}>
										Claim this listing
									</Link>
								</Button>
							</div>
						) : null}
					</div>
				</div>

				{relatedBusinesses.length > 0 && (
					<div>
						<div className="mb-3 flex items-center justify-between">
							<h3 className="font-serif text-lg tracking-tight">
								More local{' '}
								{relatedCategory ? categoryPlural(relatedCategory) : 'businesses'}{' '}
								in Dallas-Fort Worth
							</h3>
							<Button variant="outline" size="sm" asChild>
								<Link
									to={
										categoryHrefs[
											relatedBusinesses[0]?.categories[0]?.slug ?? ''
										] ??
										`/categories/${relatedBusinesses[0]?.categories[0]?.slug}`
									}
								>
									View All <Icon name="chevron-right" />
								</Link>
							</Button>
						</div>
						<BusinessListingGroup>
							{relatedBusinesses.map((rb, index) => (
								<li key={rb.id}>
									<BusinessListing
										business={rb}
										track={{
											pageType: 'business_profile',
											placement: 'related_listings',
											position: index + 1,
										}}
									/>
								</li>
							))}
						</BusinessListingGroup>
					</div>
				)}
			</div>
		</>
	)
}

interface AiService {
	service: string
	content: string
}

interface FaqItem {
	q: string
	a: string
}

function isAiServiceItem(value: unknown): value is AiService {
	if (!value || typeof value !== 'object') return false
	const v = value as Record<string, unknown>
	return typeof v.service === 'string' && typeof v.content === 'string'
}

function isFaqItem(value: unknown): value is FaqItem {
	if (!value || typeof value !== 'object') return false
	const v = value as Record<string, unknown>
	return typeof v.q === 'string' && typeof v.a === 'string'
}
